import { Avatar, Tooltip } from "@mantine/core";
import { useMemo, memo } from "react";
import "./DifficultyScore.scss";

interface DifficultyScoreProps {
  score: number;
  isProfile?: boolean;
}

/**
 * Renders a difficulty score component, which displays a score and a corresponding color.
 * The score can be "EASY", "MEDIUM", or "HARD", and the color will be green, yellow, or red, respectively.
 * If the `isProfile` prop is true, the component will render a larger score with a heading tag.
 * @param {number} score - The difficulty score
 * @param {boolean} [isProfile=false] - Whether to render the component for a profile page
 * @returns {JSX.Element} The difficulty score component
 */
const DifficultyScore = memo(
  ({ score, isProfile = false }: DifficultyScoreProps) => {
    const scoreTitle = useMemo((): string => {
      if (score === 0) {
        return "No Data";
      } else if (score > 0 && score <= 14) {
        return "Easy";
      } else if (score > 14 && score <= 45) {
        return "Moderate";
      } else {
        return "Complex";
      }
    }, [score]);

    const description = useMemo(() => {
      switch (scoreTitle) {
        case "No Data":
          return "Your client has not submitted their tax organizer.";
        case "Easy":
          return "This tax return is considered a basic return due to its simplicity.";
        case "Moderate":
          return "This tax return involves some additional complexity compared to a basic return.";
        case "Complex":
          return "This tax return requires more time and effort due to its intricate nature.";
        default:
          return "No description available.";
      }
    }, [scoreTitle]);

    const scoreColor = useMemo(() => {
      if (score === 0) {
        return "gray";
      } else if (score > 0 && score <= 14) {
        return "green";
      } else if (score > 14 && score <= 45) {
        return "orange";
      } else {
        return "red";
      }
    }, [score]);

    return (
      <div className="difficulty-container">
        <div className="difficulty-score">
          <Tooltip
            withArrow
            arrowSize={10}
            multiline
            className="difficulty-tooltip"
            label={
              <div>
                <p>{scoreTitle}</p>
                <p>{description}</p>
              </div>
            }
          >
            <Avatar
              data-testid="difficulty-score"
              color={scoreColor}
              size={isProfile ? "45" : "50"}
              styles={{
                root: {
                  border: `4px solid ${scoreColor}`,
                },
              }}
            >
              {score || "--"}
            </Avatar>
          </Tooltip>
        </div>
        {isProfile && (
          <div className="difficulty-score">
            <h3 className={`score-title ${scoreColor}-color`}>{scoreTitle}</h3>
          </div>
        )}
      </div>
    );
  }
);

DifficultyScore.displayName = "DifficultyScore";

export default DifficultyScore;
